"use client";

import { CircleHelp } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useStableId } from "@/lib/use-stable-id";
import { cn } from "@/lib/utils";

export interface InfoTipProps {
  /** Texto explicativo. Fica sempre no DOM — leitor de tela lê pelo aria-describedby. */
  children: React.ReactNode;
  /** Nome acessível do gatilho. Diga do que a dica trata, não "ajuda". */
  label: string;
  className?: string;
}

/**
 * Ícone "?" que revela uma explicação curta.
 *
 * Não usamos Radix/shadcn aqui: o projeto só tem `@radix-ui/react-slot` e um
 * Tooltip completo traria um pacote inteiro para um caso só. Mais relevante,
 * tooltip clássico depende de hover — que não existe em toque, justamente o
 * contexto em que essa dica precisa funcionar. Então o gatilho é um botão, e a
 * regra é uma só: **abre** no clique/Enter, no hover e no foco por teclado;
 * **fecha** ao o ponteiro sair do conjunto gatilho+balão, no Esc e no toque
 * fora. O texto vive no DOM e é ligado por `aria-describedby`, então
 * quem usa leitor de tela recebe a explicação junto com o gatilho, sem precisar
 * ativar nada.
 */
export function InfoTip({ children, label, className }: InfoTipProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const tipId = useStableId();

  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onPointerDown = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };

    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [open]);

  return (
    // O ponteiro é do invólucro, não do botão: o balão é irmão do gatilho, e
    // com `onMouseLeave` no botão mover o cursor do ícone para o texto fechava
    // a dica. A WCAG 1.4.13 exige que conteúdo revelado por hover possa ser
    // percorrido pelo ponteiro — o que mais importa para quem aproxima o cursor
    // ou usa ampliação para ler. Aqui os dois ficam dentro da mesma área.
    // O invólucro é região de hover, não controle: não tem clique nem papel. O
    // caminho de teclado vive no <button> abaixo, com onFocus/onBlur, e dar um
    // `role` a este span mentiria sobre o que ele é. A regra existe para pegar
    // `<div onClick>` sem equivalente de teclado, que não é o caso.
    // biome-ignore lint/a11y/noStaticElementInteractions: região de hover sem clique; teclado no <button>
    <span
      ref={wrapRef}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      className={cn("relative inline-flex align-middle", className)}
    >
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-describedby={tipId}
        // Abre, não alterna. Alternar fazia o clique fechar uma dica que o
        // hover tinha acabado de abrir — no mouse o gatilho já estava aberto
        // quando o dedo chegava. Em toque não existe hover, então o clique
        // continua sendo o que abre. Fechar é sempre sair, Esc ou toque fora.
        onClick={() => setOpen(true)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        // 24 × 24 é o mínimo da WCAG 2.5.8, e este era o único controle do
        // portal abaixo dele: 20px. O ícone continua em 16 — cresce o alvo,
        // não o desenho.
        className="inline-flex h-6 w-6 items-center justify-center rounded-full text-fg-muted transition-colors hover:bg-bg-secondary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <CircleHelp aria-hidden className="h-4 w-4" />
      </button>

      {/*
       * Sempre no DOM para o `aria-describedby` ter alvo — a spec de accname
       * manda incluir nós referenciados mesmo com `display:none`, então o
       * leitor de tela continua lendo a descrição com o balão fechado.
       *
       * Fechado precisa ser `hidden`, não `sr-only`: as duas classes declaram
       * `width`, e o `w-64` vencia a cascata. Resultado: uma caixa de 256px
       * posicionada em absoluto empurrava o documento para 541px num viewport
       * de 390 — e o `<dialog>` do menu mobile, que se posiciona pelo bloco
       * inicial, aparecia deslocado na área logada.
       *
       * Ancorado à direita porque o gatilho fica no fim de um título: crescendo
       * para a esquerda o balão nunca cria rolagem horizontal.
       */}
      {/*
       * O recuo de 8px é `pt-2` aqui, não `mt-2`: como margem ele era vão morto
       * entre o gatilho e o balão, e atravessá-lo com o ponteiro disparava o
       * `mouseleave` do conjunto — a dica fechava no caminho até o texto. Como
       * padding, os 8px pertencem ao alvo, e o percurso é contínuo.
       */}
      <span
        id={tipId}
        role="tooltip"
        className={cn(
          "absolute top-full right-0 z-30 w-64 max-w-[calc(100vw-3rem)] pt-2",
          open ? "block" : "hidden",
        )}
      >
        <span className="block rounded-lg bg-fg-strong px-3 py-2 text-body-xs leading-snug text-on-primary shadow-lg">
          {children}
        </span>
      </span>
    </span>
  );
}
